// Lesson 1.5, Teach it who you are.
//
// Approved by Leena 2026-10-09, converted mechanically from the draft.
//
// SETTINGS VERIFIED by Leena on 2026-10-09, clicking through all three live
// products, so this lesson carries no verify flag:
//   ChatGPT  correct as drafted
//   Claude   was wrong (Profile). It is Settings > Account > Instructions for Claude
//   Gemini   depends on the account type. Personal Intelligence exists on a
//            personal account and can be switched off entirely by a Workspace
//            administrator, in which case there is no custom-instructions
//            screen at all and a Gem is the fallback. The gemini.google.com
//            /saved-info "bypass" that Gemini itself suggests does not work.

export default {
  key: '1-5',
  title: 'Teach it who you are',
  minutes: 18,
  status: 'ready',
  outcome: 'Custom instructions and memory, set up once, so you never re-explain yourself.',

  why: 'If you have been following along, you have now pasted the same context into four different chats. Who you are, what the company does, how you write. That is the tax most people never stop paying, and it is why they give up. You can pay it once instead. This is the lesson that delivers the promise on the course page: an AI that already knows how you work.',

  blocks: [
    {
      type: 'concept',
      heading: 'Three different things that sound the same',
      body: [
        { type: 'deflist', items: [
          { term: 'Custom instructions', text: 'Standing orders you write once. Who you are, what you do, how you want answers. Applied to every new chat. This is the one that earns its keep.' },
          { term: 'Memory', text: 'Things it notices and keeps on its own as you work. Useful, slightly unpredictable, and worth leaving on. Lesson 1.2 covered why memory is the part to keep.' },
          { term: 'Projects or spaces', text: 'A folder with its own instructions and files, for one ongoing piece of work. Use these when one client or project needs context that would be noise everywhere else.' },
        ] },
        { type: 'p', text: 'The mistake people make with custom instructions is writing a personality brief. "Be concise and friendly." That does very little. What works is the boring operational detail it cannot guess.' },
        { type: 'callout', tone: 'info', text: 'Weak: "I work in sales. Be helpful and concise."' },
        { type: 'callout', tone: 'info', text: 'Worked: "I am an account manager at a metals distributor in Miami. My customers are fabricators and contractors buying aluminium and stainless by the sheet. I write short, direct emails and I never use exclamation marks. When I ask for a draft, give me one version, not three options. If a number matters, flag that I need to check it rather than guessing. Never invent a product spec."' },
        { type: 'p', text: 'Every sentence in the second one removes a round trip you would otherwise have had.' },
      ],
    },

    {
      type: 'tabs',
      heading: 'Where the setting lives',
      intro: 'Expect these to have moved. This is the part of the course with the shortest shelf life. All three products reorganise their settings regularly, sometimes within a month. What is below was right when we wrote it. If your menus look different, the thing you are looking for is almost certainly one level up or down from where we point, under a heading about personalisation, memory or profile. Tell us in the box at the end and we will correct it.',
      tabs: [
        { tool: 'ChatGPT', steps: [
          'Settings, then Personalization, then Custom instructions.',
          'Two boxes: what you want it to know about you, and how you want it to respond. Put the operational detail in the first and your format rules in the second.',
          'Memory lives in the same Personalization area and can be reviewed and edited.',
        ] },
        { tool: 'Claude', steps: [
          'Settings, then Account, then Instructions for Claude. It tells you these are kept in mind across all your chats, which is exactly what you want.',
          'Projects, for one ongoing piece of work. A project has its own instructions and its own files, which is the better home for one client’s context.',
        ] },
        { tool: 'Gemini', steps: [
          'On a personal Google account: Settings and help, then Personal Intelligence, then Instructions for Gemini, then the plus button to add a rule. Memory and connected apps live in the same place.',
          'On a work or school account, this may not be there at all. Your administrator can switch Personal Intelligence off for the whole organisation, and when they have, there is no custom instructions screen to find. If your settings list stops at activity, connected apps, scheduled actions, gems and themes, that is what has happened, and it is not something you can work around.',
          'What to do instead: make a Gem. A Gem is a reusable assistant that carries its own instructions, so you can put the same four to six sentences in there and start from the Gem each time. It is a little more friction than standing instructions, and it is the best available option on a restricted account. Gems get their own lesson in a later course.',
        ] },
      ],
      callout: {
        tone: 'rule',
        text: 'One warning worth more than the instructions above. We asked Gemini itself where its custom instructions live. It gave a confident answer with a menu that did not exist on that account, then offered a direct link as a workaround that simply redirected to the home page. Neither was true. This is lesson 1.2 happening in real life: a tool will describe its own settings wrongly, with total confidence, and you only find out by going and looking. Trust what you can see on your screen over what any of them tells you about itself.',
      },
    },

    {
      type: 'exercise',
      heading: 'Write yours',
      steps: [
        { id: 'write_instructions', text: 'Write four to six sentences covering: your job and who your customers are, how you write, what format you want answers in, and one thing it must never do. Use the worked example above as the shape.' },
        { id: 'save_instructions', text: 'Paste it into the setting for your tool and save it. If your account does not have the setting, make a Gem instead and put it there.' },
        { id: 'test_fresh_chat', text: 'Open a brand new chat and ask for something you asked for earlier in the course, without any of the context you pasted last time. Tick the box when the answer comes back already in your voice. That moment is the whole point of the lesson.' },
      ],
    },

    {
      type: 'quickcheck',
      questions: [
        {
          q: 'Which belongs in custom instructions?',
          options: ['"Be professional and thorough"', '"Give me one version, not three options"'],
          answer: 1,
          because: 'The second removes a round trip every single time. The first is a mood.',
        },
        {
          q: 'One client needs a lot of background that would be noise everywhere else. Where does it go?',
          options: ['Custom instructions', 'A project or space for that client', 'Pasted into each chat'],
          answer: 1,
          because: 'Custom instructions apply to everything, so client-specific context belongs in a project.',
        },
        {
          q: 'You turned training off in lesson 1.2. Should memory be off too?',
          options: ['Yes, same thing', 'No, memory is the part worth keeping'],
          answer: 1,
          because: 'Training is the company learning from you. Memory is your assistant learning about you. Let it learn about you, not from you.',
        },
      ],
    },

    {
      type: 'promptcard',
      title: 'Check what you know about me',
      body: 'Before we start, tell me back what you already know about me and how I like to work, from your instructions and anything you remember. Flag anything that looks out of date or wrong so I can fix it.',
      note: 'Run this every month or so. Memory drifts, and a stale fact quietly degrades everything.',
    },

    {
      type: 'takeaway',
      text: 'Write the boring operational detail down once. Every chat after that starts where you left off.',
    },
  ],

  videoNote: 'Leena walks through the settings.',
};
